// src/store/slices/formsSlice.ts
// Utworzony w Krok 3

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { FormTemplateDetail, FormsState, FormData, ValidationErrors } from '../../types/forms';

// Async Thunks
export const fetchFormTemplate = createAsyncThunk(
  'forms/fetchFormTemplate',
  async (formTemplateId: number, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/forms/${formTemplateId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        return rejectWithValue(`HTTP ${response.status}: ${errorText}`);
      }

      const data: FormTemplateDetail = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
);

// =============================================================================================
// ==== Zmiana w Krok 4B >>>>
// ==== z:
// export const submitForm = createAsyncThunk(
//   'forms/submitForm',
//   async ({ formTemplateId, formData }: { formTemplateId: number; formData: FormData }, { rejectWithValue }) => {
//     try {
//       const token = localStorage.getItem('token');
//       const response = await fetch(`http://localhost:5218/api/forms/${formTemplateId}/submit`, {
//         method: 'POST',
//         headers: {
//           'Authorization': `Bearer ${token}`,
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify(formData),
//       });

//       if (!response.ok) {
//         const errorText = await response.text();
//         return rejectWithValue(`HTTP ${response.status}: ${errorText}`);
//       }

//       const data = await response.json();
//       return data;
//     } catch (error) {
//       return rejectWithValue(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
//     }
//   }
// );
// ==== na:
export const submitForm = createAsyncThunk(
  'forms/submitForm',
  async (
    { 
      nodeId, 
      formTemplateId, 
      formData, 
      responses 
    }: { 
      nodeId: number; 
      formTemplateId: number; 
      formData: FormData; 
      responses: Record<string, any>; 
    }, 
    { rejectWithValue }
  ) => {
    try {
      const token = localStorage.getItem('token');
      
      // Create submission payload matching backend expectations
      const submissionData = {
        nodeId,
        formTemplateId,
        formData,
        responses
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/forms/${formTemplateId}/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        return rejectWithValue(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
);
// <<<< Zmiana w Krok 4B ====
// =============================================================================================




// Initial State
const initialState: FormsState = {
  currentForm: null,
  formData: {},
  validationErrors: {},
  isLoading: false,
  isSubmitting: false,
  submitSuccess: false,
  error: null,
};

// Slice
const formsSlice = createSlice({
  name: 'forms',
  initialState,
  reducers: {
    updateFormField: (state, action: PayloadAction<{ sectionId: string; value: any }>) => {
      const { sectionId, value } = action.payload;
      state.formData[sectionId] = value;
      
      // Clear validation error for this field
      if (state.validationErrors[sectionId]) {
        delete state.validationErrors[sectionId];
      }
    },
    
    setValidationError: (state, action: PayloadAction<{ sectionId: string; error: string }>) => {
      const { sectionId, error } = action.payload;
      state.validationErrors[sectionId] = error;
    },
    
    clearValidationErrors: (state) => {
      state.validationErrors = {};
    },
    
    resetForm: (state) => {
      state.formData = {};
      state.validationErrors = {};
      state.submitSuccess = false;
      state.error = null;
    },
    
    clearCurrentForm: (state) => {
      state.currentForm = null;
      state.formData = {};
      state.validationErrors = {};
      state.submitSuccess = false;
      state.error = null;
    },

    // =============================================================================================
    // ==== Dodane w Krok 4B >>>>
    clearSubmitState: (state) => {
      state.isSubmitting = false;
      state.submitSuccess = false;
      state.error = null;
    },
    // <<<< Dodane w Krok 4B ====
    // =============================================================================================

  },
  extraReducers: (builder) => {
    // Fetch Form Template
    builder
      .addCase(fetchFormTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFormTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentForm = action.payload;
        state.formData = {};
        state.validationErrors = {};
        state.error = null;
      })
      .addCase(fetchFormTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.currentForm = null;
      });

    // Submit Form
    builder
      .addCase(submitForm.pending, (state) => {
        state.isSubmitting = true;
        state.submitSuccess = false;
        state.error = null;
      })
      .addCase(submitForm.fulfilled, (state) => {
        state.isSubmitting = false;
        state.submitSuccess = true;
        state.error = null;
      })
      .addCase(submitForm.rejected, (state, action) => {
        state.isSubmitting = false;
        state.submitSuccess = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  updateFormField,
  setValidationError,
  clearValidationErrors,
  resetForm,
  clearCurrentForm,
  // =============================================================================================
  // ==== Dodane w Krok 4B >>>>
  clearSubmitState,
  // <<<< Dodane w Krok 4B ====
  // =============================================================================================

} = formsSlice.actions;

export default formsSlice.reducer;